import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import { fromUnixTime, differenceInWeeks } from 'date-fns';
import { Trans } from '@lingui/react';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import ButtonFavoriteVideoCard from '@components/ButtonFavoriteVideoCard';
import { StyledMaterialButton } from '@components/buttons';
import Icon from '@components/Icon';
import ButtonAddtoPlaylist from '@ecosystems/ButtonAddtoPlaylist';
import ButtonScheduleModal from '@ecosystems/ButtonScheduleModal';
import GenreInstructorMetadata from '@ecosystems/GenreInstructorMetadata';
import MenuItemShareModal from '@ecosystems/MenuItemShareModal';
import theme from '@lib/theme';
import Card from '@organisms/cards/Card';
import LanguageContext from '@lib/contexts/languageContext';
import HeadSetIcon from '@assets/HeadSetIcon';

const BottomRightWrapper = styled.span`
  position: absolute;
  right: 6px;
  bottom: 6px;
`;

const DurationWrapper = styled.span`
  background-color: rgba(0, 0, 0, 0.75);
  font-size: 11px;
  font-weight: 500;
  color: ${props => props.theme.colors.neutrals.white};
  text-align: center;
  border-radius: 4px;
  padding: 5px 7px;
  display: block;
`;

const Badge = styled.span<{ backgroundColor: string }>`
  display: flex;
  color: ${props => props.theme.colors.neutrals['white']};
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  border-radius: 5px;
  padding: 4px;
  margin-right: 5px;
  background-color: ${props => props.backgroundColor};
`;

const CenterWrapper = styled.div`
  position: absolute;
  width: 19px;
  height: 19px;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  border: 2px solid #fff;
  border-radius: 50%;
  padding: 29px;
  box-shadow: 0;
  transition: all 0.3s;
`;

const PlayIconWrapper = styled.div`
  position: absolute;
  top: 55%;
  left: 54%;
  transform: translate(-50%, -50%);
  z-index: 1;
`;

const TopLeftWrapper = styled.div`
  position: absolute;
  top: 5px;
  left: 5px;
  display: flex;
`;

const ThumbnailWrapper = styled.div<{ locked?: boolean; progress: number }>`
  align-items: center;
  justify-content: center;
  display: flex;
  width: 100%;
  height: 100%;

  &:hover {
    cursor: pointer;
  }

  &:hover > .changeBackgroundOnHover {
    box-shadow: 0px 0px 14px 0px rgba(0, 0, 0, 0.75);
  }

  ${props =>
    props.locked
      ? `
          &:before {
            content: '';
            opacity: 0.8;
            background: ${props.theme.colors.secondary['green-4']};
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
          }
        `
      : null};

  :after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    height: 4px;
    width: ${({ progress }) => progress || 0}%;
    background-color: #129edc;
  }
`;

const TagsWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: start;
  height: 100%;
  width: 100%;
  padding: 10px;
`;

const VideoTag = styled.span`
  color: ${props => props.theme.colors.neutrals.white};
  border-radius: 3px;
  padding: 5px;
  margin-right: 8px;
  background-color: ${props => props.theme.colors.secondary.blue};
`;

const VideoCard = ({
  className = '',
  video,
  large = false,
  locked = false,
  redirectTo = null,
  onClick = null,
  tags = [],
  disableActionButtons = false,
  ...otherProps
}) => {
  const locale = useContext(LanguageContext);
  const [anchorElMenu, setAnchorElMenu] = useState(null);
  const [rightMenuOpen, setRightMenuOpen] = useState(false);

  const handleOpenMenu = event => {
    setAnchorElMenu(event.currentTarget);
    setRightMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setRightMenuOpen(false);
    setAnchorElMenu(null);
  };

  const now = new Date();
  const publishedAt = fromUnixTime(video.publishedAt);
  const isNew = differenceInWeeks(now, publishedAt) <= 12;
  const isAudio = video.kind === 'audio';

  const dataProps = Object.entries(otherProps)
    .filter(([key]) => key.startsWith('data-'))
    .reduce((object, [key, value]) => {
      object[key] = value;
      return object;
    }, {});

  const progress =
    video?.cursorAt > 0
      ? Math.floor(((video?.cursorAt / 60) * 100) / video?.duration)
      : 0;

  return (
    <Card
      className={className}
      large={large}
      onClick={onClick}
      redirectTo={
        !onClick
          ? locked
            ? null
            : redirectTo
            ? redirectTo
            : `/${locale}/videos/${video.identifier}`
          : null
      }
      thumbnailUrl={video.thumbnailUrl}
      thumbnailContent={
        <ThumbnailWrapper locked={locked} progress={progress}>
          {!locked ? (
            <>
              <CenterWrapper className="changeBackgroundOnHover">
                <PlayIconWrapper>
                  <Icon name="ygb-icon-play" color="white" fontSize="17.5px" />
                </PlayIconWrapper>
              </CenterWrapper>
            </>
          ) : (
            <Icon
              name="ygb-icon-programlock-ic"
              color="white"
              fontSize="30px"
              style={{ zIndex: 1 }}
            />
          )}
          <TopLeftWrapper>
            {isNew ? (
              <Badge backgroundColor={theme.colors.primary['blue-2']}>
                <Trans id="metadata.new" />
              </Badge>
            ) : null}
            {video.flavor && video.flavor != 'Tutorial' ? (
              <Badge backgroundColor="#454857">{video.flavor}</Badge>
            ) : null}
            {isAudio ? (
              <Badge backgroundColor="#454857">
                <img width="14px" src="/static/icons/HeadSetIcon.svg" />
              </Badge>
            ) : null}
          </TopLeftWrapper>
          <BottomRightWrapper>
            <DurationWrapper>
              {video.duration} <Trans id="metadata.min" />
            </DurationWrapper>
          </BottomRightWrapper>
          {tags && (
            <TagsWrapper>
              {tags?.map(tag => (
                <VideoTag key={tag}>{tag}</VideoTag>
              ))}
            </TagsWrapper>
          )}
        </ThumbnailWrapper>
      }
      contentTitle={video.title}
      contentAuthor={
        <GenreInstructorMetadata
          genre={video.genreName}
          genreSlug={video.genreSlug}
          primaryStyle={video.primaryStyle}
          instructors={video.instructors}
        />
      }
      contentDescription={video.description}
      contentCardLeftSection={
        disableActionButtons ? null : (
          <ButtonFavoriteVideoCard videoId={video.id} />
        )
      }
      contentCardRightSection={
        disableActionButtons ? null : (
          <React.Fragment>
            <StyledMaterialButton
              onClick={handleOpenMenu}
              data-testid="VideoCard--OtherActionsButton"
            >
              <Icon name="ygb-icon-menu-hor" fontSize="5px" />
            </StyledMaterialButton>
            {rightMenuOpen ? (
              <Menu
                id="video-card-right-menu"
                anchorEl={anchorElMenu}
                open={rightMenuOpen}
                onClose={handleCloseMenu}
                keepMounted
              >
                <ButtonAddtoPlaylist
                  tag={MenuItem}
                  videoId={video.id}
                  handleCloseParentMenu={handleCloseMenu}
                  anchorElMenu={anchorElMenu}
                  handleOpenParentMenu={() => {
                    handleOpenMenu({
                      currentTarget: anchorElMenu,
                    });
                  }}
                  onModalClose={handleCloseMenu}
                />
                <ButtonScheduleModal
                  tag={MenuItem}
                  resource="video"
                  resourceId={video.id}
                  onModalClose={handleCloseMenu}
                />
                <MenuItemShareModal
                  resourceIdentifier={video.identifier}
                  resource="videos"
                  onModalClose={handleCloseMenu}
                />
              </Menu>
            ) : null}
          </React.Fragment>
        )
      }
      contentLeftCardLargeSection={
        disableActionButtons
          ? []
          : [
              <ButtonFavoriteVideoCard key={1} videoId={video.id} />,
              <ButtonAddtoPlaylist
                key={2}
                tag={StyledMaterialButton}
                videoId={video.id}
                handleCloseParentMenu={handleCloseMenu}
              />,
              <ButtonScheduleModal
                key={3}
                tag={StyledMaterialButton}
                resource="video"
                resourceId={video.id}
              />,
            ]
      }
      contentRightCardLargeSection={
        disableActionButtons ? null : (
          <React.Fragment>
            <StyledMaterialButton onClick={handleOpenMenu}>
              <Icon name="ygb-icon-menu-hor" fontSize="5px" />
            </StyledMaterialButton>
            <Menu
              id="video-card-menu"
              anchorEl={anchorElMenu}
              keepMounted
              open={Boolean(anchorElMenu)}
              onClose={handleCloseMenu}
            >
              <MenuItemShareModal
                resourceIdentifier={video.identifier}
                resource="videos"
              />
            </Menu>
          </React.Fragment>
        )
      }
      {...dataProps}
    />
  );
};

export default VideoCard;
